import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import styled from "styled-components";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Download from "yet-another-react-lightbox/plugins/download";

import SearchBar from "@/components/SearchBar";
import Result from "./Result";
import { api } from "@/utils/api";
import {
  ImageLibraryImageResource,
  PaginatedResponse,
} from "@/types/resources";
import PulsingLoader from "@/components/PulsingLoader";
import Text from "@/components/Text";
import InfiniteLoader from "@/components/InfiniteLoader";
import useDebounce from "@/hooks/useDebounce";

const ResultsBox = styled.div`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const LoaderBox = styled.div`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  padding: 24px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Loader = styled.div`
  text-align: center;
`;

const NoResultsText = styled(Text)`
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: center;
  opacity: 0.5;
  padding: 20px 0;
  svg {
    margin-right: 6px;
    transform: translateY(-1px);
  }
`;

const Results = () => {
  const [search, setSearch] = useState("");

  const [index, setIndex] = useState(-1);

  const debouncedSearch = useDebounce(search, 500);

  const fetchImages = ({ pageParam = "/v1/image-library" }) => {
    return api.get<never, PaginatedResponse<ImageLibraryImageResource>>(
      pageParam,
      {
        params: {
          ...(!!debouncedSearch && {
            "filter[search]": debouncedSearch,
          }),
        },
      }
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<ImageLibraryImageResource>>(
    ["images_library", { debouncedSearch }],
    fetchImages,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  const results = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<ImageLibraryImageResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );
  return (
    <>
      <Lightbox
        render={{
          iconNext: () => (
            <svg
              width="56"
              height="56"
              viewBox="0 0 56 56"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28C0 43.464 12.536 56 28 56Z"
                fill="white"
                fillOpacity="0.35"
              />
              <path
                d="M25.5 35.3045L32 28.0001L25.5 20.6958"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          iconPrev: () => (
            <svg
              width="56"
              height="56"
              viewBox="0 0 56 56"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M28 56C12.536 56 0 43.464 0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56Z"
                fill="white"
                fillOpacity="0.35"
              />
              <path
                d="M30.5 35.3045L24 28.0001L30.5 20.6958"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        }}
        styles={{
          root: {
            "--yarl__color_backdrop": "rgba(0, 0, 0, .25)",
            "--yarl__carousel_padding_px": 40,
          },
        }}
        open={index >= 0}
        close={() => setIndex(-1)}
        slides={results.map((image) => {
          return {
            src: image.versions.original,
            download: `${image.versions.original}?download`,
          };
        })}
        index={index}
        plugins={[Download]}
      />
      <SearchBar
        setValue={setSearch}
        value={search}
        placeholder="Search by prompt name"
      />
      {isLoading || isError ? (
        <LoaderBox>
          {isLoading && (
            <Loader>
              <PulsingLoader />
              <Text $color="gray" $type="captionSemibold">
                Please be patient, we are getting your images
              </Text>
            </Loader>
          )}
          {isError && (
            <Text $type="caption" $color="gray">
              An error occurred!
            </Text>
          )}
        </LoaderBox>
      ) : (
        <>
          {!!results.length ? (
            <ResultsBox>
              {results.map((image, index) => (
                <Result
                  image={image}
                  key={image.id}
                  onClick={() => setIndex(index)}
                />
              ))}
            </ResultsBox>
          ) : (
            <LoaderBox>
              <div>
                <NoResultsText $type="caption" $color="gray">
                  <svg
                    width="31"
                    height="31"
                    viewBox="0 0 31 31"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.5011 9.76001H26.1083C26.364 9.76001 26.6093 9.86115 26.7901 10.0412C26.971 10.2212 27.0725 10.4654 27.0725 10.72V24.16C27.0725 24.4146 26.971 24.6588 26.7901 24.8388C26.6093 25.0189 26.364 25.12 26.1083 25.12H4.89397C4.63846 25.1193 4.39362 25.0179 4.21295 24.838C4.03227 24.6581 3.93044 24.4144 3.92969 24.16V12.64"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.3225 12.64H3.92969V7.84C3.92969 7.5854 4.03128 7.34122 4.21212 7.16118C4.39296 6.98115 4.63823 6.88 4.89397 6.88H11.3225C11.5312 6.88 11.7342 6.94738 11.9011 7.072L15.5011 9.76L11.9011 12.448C11.7342 12.5726 11.5312 12.64 11.3225 12.64Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  No Images found
                </NoResultsText>
              </div>
            </LoaderBox>
          )}
        </>
      )}
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default Results;
